<template>
    <div class="h-100">
        <div ref="panoramaElement"
             v-if="panoramaAvailable"
             class="w-100 h-100"/>
        <div v-else class="d-flex flex-column w-100 h-100 justify-content-center">
            <div class="alert alert-warning text-center">
                <p>
                    Panorama pro tuto lokalitu není k dispozici
                </p>
                <a :href="`https://mapy.cz/zakladni?source=coor&id=${longitude},${latitude}`"
                   target="_blank" title="Zobrazit na webu Mapy.cz">
                    Zobrazit na webu Mapy.cz
                </a>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {loadMapyCz} from '@/components/maps/mapyCz';
import {IOutput} from 'mapy-cz';
import {defineComponent, onBeforeUnmount, onMounted, ref, watch} from 'vue';

interface ShownPanorama {
    output: IOutput;
    host: HTMLElement;
}

interface CreatedContext {
    canvas: HTMLCanvasElement;
    context: WebGLRenderingContext;
}

/**
 * Calls the function and returns the WebGL contexts it created synchronously.
 */
function trackCreatedContexts<T>(call: () => T): {result: T; contexts: CreatedContext[]} {
    const contexts: CreatedContext[] = [];
    const getContext = HTMLCanvasElement.prototype.getContext;

    HTMLCanvasElement.prototype.getContext = function (this: HTMLCanvasElement, ...args: Parameters<typeof getContext>) {
        const context = getContext.apply(this, args);
        if (context !== null && 'getExtension' in context && !contexts.some(created => created.context === context)) {
            contexts.push({canvas: this, context: context as WebGLRenderingContext});
        }

        return context;
    } as typeof getContext;

    try {
        return {result: call(), contexts};
    } finally {
        HTMLCanvasElement.prototype.getContext = getContext;
    }
}

function loseContexts(contexts: CreatedContext[]): void {
    contexts
        .filter(created => !created.context.isContextLost())
        .forEach(created => created.context.getExtension('WEBGL_lose_context')?.loseContext());
}

export default defineComponent({
    props: {
        longitude: {
            type: Number,
            required: true,
        },
        latitude: {
            type: Number,
            required: true,
        },
    },
    setup(props) {
        const panoramaElement = ref<HTMLElement | null>(null);
        const panoramaAvailable = ref(true);

        const apiKey = process.env.VUE_APP_API_KEY_MAPY_CZ;

        let panorama: ShownPanorama | null = null;
        let hiddenPanorama: ShownPanorama | null = null;
        // Incremented whenever the panorama is hidden, a load started earlier is discarded when it finishes
        let loadGeneration = 0;
        let loadingGeneration: number | null = null;
        let showTimeout: number | null = null;
        let hideTimeout: number | null = null;
        let visibilityObserver: IntersectionObserver | null = null;

        function release(shown: ShownPanorama): void {
            // The library leaves its WebGL context to the garbage collector. Safari keeps only a few
            // contexts per page and takes them away from the oldest panoramas, which the library reports
            // by a dialog over the whole page. Losing the context after destroy() frees it right away
            // and shows no dialog, because destroy() removes the library's listener.
            const canvases = Array.from(shown.host.querySelectorAll('canvas'));
            shown.output.destroy();
            canvases.forEach(canvas => canvas.getContext('webgl')?.getExtension('WEBGL_lose_context')?.loseContext());
            shown.host.remove();
        }

        function releaseHiddenPanorama(): void {
            if (hideTimeout !== null) {
                clearTimeout(hideTimeout);
                hideTimeout = null;
            }

            if (hiddenPanorama !== null) {
                release(hiddenPanorama);
                hiddenPanorama = null;
            }
        }

        function hide(): void {
            loadGeneration++;

            if (panorama === null) {
                return;
            }

            releaseHiddenPanorama();
            hiddenPanorama = panorama;
            panorama = null;
            hideTimeout = window.setTimeout(releaseHiddenPanorama, 100);
        }

        async function show(generation: number): Promise<void> {
            loadingGeneration = generation;
            try {
                await createPanorama(generation);
            } finally {
                if (loadingGeneration === generation) {
                    loadingGeneration = null;
                }
            }
        }

        async function createPanorama(generation: number): Promise<void> {
            const Panorama = await loadMapyCz();
            if (generation !== loadGeneration || panoramaElement.value === null) {
                return;
            }

            // Each panorama gets its own host, the library clears the whole parent on destroy()
            const host = document.createElement('div');
            host.classList.add('w-100', 'h-100');
            panoramaElement.value.appendChild(host);

            // For every panorama the library also checks WebGL support on a canvas of its own and leaves
            // that context to the garbage collector. Such contexts would take the place of the shown
            // panoramas in Safari, so the ones outside the host are lost. The panorama is created
            // synchronously at the start of the call, a library that stops doing so leaves nothing to lose.
            const {result, contexts} = trackCreatedContexts(() => Panorama.panoramaFromPosition({
                parent: host,
                lon: props.longitude,
                lat: props.latitude,
                apiKey: apiKey as string,
                showNavigation: true,
                yaw: 'point',
            }));
            const output = await result;
            loseContexts(contexts.filter(created => !host.contains(created.canvas)));

            if (generation !== loadGeneration) {
                release({output, host});
                return;
            }

            if (output.errorCode === 'PANORAMA_NOT_FOUND') {
                release({output, host});
                panoramaAvailable.value = false;
                return;
            }

            panorama = {output, host};
        }

        function visibilityChanged(isVisible: boolean): void {
            if (showTimeout !== null) {
                clearTimeout(showTimeout);
                showTimeout = null;
            }

            if (!isVisible) {
                hide();
                return;
            }

            if (panorama !== null || loadingGeneration === loadGeneration) {
                return;
            }

            const generation = loadGeneration;
            showTimeout = window.setTimeout(() => {
                showTimeout = null;
                show(generation).catch(() => {
                    // The script failed to load, the next showing tries it again
                });
            }, 500);
        }

        function setupObserver(): void {
            if (!panoramaElement.value) {
                return;
            }

            visibilityObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    visibilityChanged(entry.isIntersecting);
                });
            }, {threshold: 0.6});

            visibilityObserver.observe(panoramaElement.value);
        }

        onMounted(() => {
            setupObserver();
        });

        watch(panoramaElement, () => {
            visibilityObserver?.disconnect();
            setupObserver();
        });

        onBeforeUnmount(() => {
            visibilityObserver?.disconnect();
            if (showTimeout !== null) {
                clearTimeout(showTimeout);
            }
            hide();
            releaseHiddenPanorama();
        });

        return {panoramaElement, panoramaAvailable};
    },
});
</script>
